/**
 * Knowledge `attributes` parsing.
 *
 * `UserKnowledge.attributes` is an AWSJSON field, which the client receives as
 * a JSON string: `{}` for a plain entry, `{"version":"1.0.0","tags":[...]}` for a
 * skill. Historically the resolver handed that string back doubly encoded
 * (`"{}"`), and a client that saved it unchanged stored a JSON *string* in the
 * database, wrapping the value one layer deeper on every save (one entry
 * reached 18 layers). The resolvers now return single-encoded JSON and unwrap
 * anything they are asked to store, and a migration repairs the stored rows,
 * but a value read before those ship, or from a row the migration has not
 * reached yet, can still carry extra layers. These helpers peel every layer so
 * the UI shows a skill's version and tags whatever the stored depth, and never
 * writes a wrapped value back.
 */

export type KnowledgeAttributes = Record<string, unknown>;

/** How many nested string layers to unwrap; the deepest row seen held 18. */
const MAX_UNWRAP_DEPTH = 32;

/**
 * Parse an `attributes` value into an object, unwrapping nested JSON-string
 * layers. Returns `{}` for null/undefined/empty input, for malformed JSON, and
 * for a value that is not a JSON object.
 */
export function parseKnowledgeAttributes(
  raw: string | null | undefined
): KnowledgeAttributes {
  if (raw == null || raw === "") {
    return {};
  }
  let value: unknown = raw;
  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth += 1) {
    if (typeof value !== "string") {
      break;
    }
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as KnowledgeAttributes;
  }
  return {};
}

/**
 * The single-encoded JSON text to send in `AddUserKnowledgeInput.attributes`
 * when saving an entry back without editing its attributes (e.g. a move to
 * another folder): the parsed object re-serialized once, so no extra layer is
 * ever written.
 */
export function serializeKnowledgeAttributes(
  raw: string | null | undefined
): string {
  return JSON.stringify(parseKnowledgeAttributes(raw));
}
